import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../componentes/Plantilla/Plantilla';
import ContactSection from '../../componentes/Secciones/Contacto/SeccionContacto';
import VideoPlayer from '../../componentes/UI/VideoPlayer';
import ComparadorImagenes from '../../componentes/UI/ComparadorImagenes';
import Breadcrumbs from '../../componentes/UI/Breadcrumbs';
import styles from './LimpiezaTuberia.module.css';

const BASE = '/imagenes/servicios/limpieza-tuberia';

const useReveal = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.1 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
};

const equipos = [
  { nombre: 'Intercambiadores de calor', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="28" height="28"><path d="M3 6h18M3 12h18M3 18h18"/><path d="M7 3v18M17 3v18"/></svg> },
  { nombre: 'Evaporadores', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="28" height="28"><path d="M12 2C6.5 2 4 6 4 10c0 5.4 4 10 8 10s8-4.6 8-10c0-4-2.5-8-8-8z"/><path d="M12 6v6l4 2"/></svg> },
  { nombre: 'Chillers', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="28" height="28"><rect x="3" y="3" width="18" height="18" rx="3"/><path d="M12 7v10M7 12h10"/><circle cx="12" cy="12" r="2"/></svg> },
  { nombre: 'Calderas pirotubulares y acuotubulares', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="28" height="28"><path d="M4 20h16M6 20V8a2 2 0 012-2h8a2 2 0 012 2v12"/><path d="M8 12h8M8 16h8"/><path d="M12 6V4"/></svg> },
  { nombre: 'Condensadores', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="28" height="28"><circle cx="12" cy="12" r="8"/><path d="M8 12h8M12 8l4 4-4 4"/></svg> },
  { nombre: 'Calentadores de jugo', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="28" height="28"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4"/><circle cx="12" cy="12" r="5"/></svg> },
  { nombre: 'Tachos de concentración', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="28" height="28"><path d="M5 3h14l-2 12H7L5 3z"/><path d="M5 3H3M19 3h2M7 15l1 6h8l1-6"/></svg> },
  { nombre: 'Sistemas de drenaje industrial', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="28" height="28"><path d="M3 17l4-10 4 6 3-4 4 8"/><path d="M3 21h18"/></svg> },
];

const beneficios = [
  { valor: '↓ Paradas', desc: 'Reducción de paradas no programadas en su línea de producción', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg> },
  { valor: '↓ Energía', desc: 'Ahorro energético por menor resistencia térmica en tuberías limpias', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg> },
  { valor: '↓ Costos', desc: 'Reducción en costos de mantenimiento correctivo y mano de obra', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg> },
];

const pasos = [
  { num: '01', titulo: 'Inspección inicial', sub: 'Boroscopía industrial', desc: 'Inspección interna mediante boroscopio industrial para identificar el tipo y grado de incrustación presente.' },
  { num: '02', titulo: 'Medición geométrica', sub: 'Parámetros del sistema', desc: 'Medición del diámetro interno de los tubos para seleccionar el equipo y accesorios adecuados para la limpieza.' },
  { num: '03', titulo: 'Requisitos de conexión', sub: 'Preparación del sitio', desc: 'Agua: caudal mínimo de 10 GPM, tubería 3/4" o 1". Electricidad: trifásica o monofásica 208–480 V.' },
  { num: '04', titulo: 'Configuración del sistema', sub: 'Pruebas preliminares', desc: 'Pruebas con diversas boquillas de alta presión para determinar la configuración óptima según tipo de suciedad y geometría.' },
  { num: '05', titulo: 'Verificación intermedia', sub: 'Segunda boroscopía', desc: 'Segunda inspección boroscópica para verificar la eficacia de la limpieza previa a la ejecución integral del servicio.' },
  { num: '06', titulo: 'Ejecución completa', sub: 'Sistema hidrodinámico', desc: 'Limpieza completa de todos los tubos mediante sistema hidrodinámico de alta presión una vez validada la configuración.' },
];

const fotos = [
  `${BASE}/foto-01.jpeg`,
  `${BASE}/foto-02.jpeg`,
  `${BASE}/foto-03.jpeg`,
  `${BASE}/foto-04.jpeg`,
  `${BASE}/foto-05.jpeg`,
  `${BASE}/foto-06.jpeg`,
];

const LimpiezaTuberia = () => {
  const [heroRef, heroVis] = useReveal();
  const [objRef, objVis] = useReveal();
  const [alcRef, alcVis] = useReveal();
  const [procRef, procVis] = useReveal();
  const [vidRef, vidVis] = useReveal();
  const [galRef, galVis] = useReveal();

  const [lightboxSrc, setLightboxSrc] = useState(null);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') setLightboxSrc(null); };
    if (lightboxSrc) {
      window.addEventListener('keydown', handleKey);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [lightboxSrc]);

  return (
    <Layout>

      {/* ── HERO ── */}
      <section
        ref={heroRef}
        className={styles.hero}
        style={{ backgroundImage: `url(${BASE}/img-02.jpg)` }}
      >
        <div className={styles.heroOverlay} />
        <div className={`container ${styles.heroContent}`}>
          <Breadcrumbs 
            paths={[
              { label: 'Inicio', to: '/' },
              { label: 'Servicios', to: '/servicios' },
              { label: 'Limpieza de Tubería Industrial' }
            ]}
          />
          <h1 className={`${styles.heroTitle} ${heroVis ? styles.titleVis : ''}`}>
            Limpieza de<br/><span className={styles.acento}>Tubería Industrial</span>
          </h1>
          <div className={`${styles.heroLine} ${heroVis ? styles.lineVis : ''}`} />
          <p className={`${styles.heroSub} ${heroVis ? styles.subVis : ''}`}>
            Remoción de incrustaciones internas mediante hidrodinámica de alta presión para optimizar la eficiencia operativa de sus equipos térmicos.
          </p>
          <div className={`${styles.heroCtas} ${heroVis ? styles.ctasVis : ''}`}>
            <a href="#contact" className="btn btn-primary">Solicitar cotización</a>
            <Link to="/servicios" className={styles.backLink}>← Todos los servicios</Link>
          </div>
        </div>
      </section>

      {/* ── OBJETIVO + FOTO CAMPO ── */}
      <section ref={objRef} className={styles.objetivoSection}>
        <div className="container">
          <div className={`${styles.objetivoGrid} ${objVis ? styles.gridVis : ''}`}>
            <div className={styles.objetivoTexto}>
              <h2 className={styles.secTitle}>
                Eficiencia operativa<br/>
                <span className={styles.acento}>maximizada</span>
              </h2>
              <div className={styles.secLine} />
              <p className={styles.objetivoDesc}>
                La limpieza de tuberías industriales tiene como finalidad optimizar la eficiencia operativa de los equipos térmicos mediante la remoción de incrustaciones internas, resultando en una disminución de paradas no programadas, ahorro energético y reducción de costos de mantenimiento.
              </p>
              <div className={styles.specsBar}>
                <div className={styles.specItem}>
                  <span className={styles.specValor}>hasta 100 m</span>
                  <span className={styles.specLabel}>Longitud de tubo</span>
                </div>
                <div className={styles.specDivider} />
                <div className={styles.specItem}>
                  <span className={styles.specValor}>3/16" – 12"</span>
                  <span className={styles.specLabel}>Diámetro interno</span>
                </div>
              </div>
            </div>
            <div className={styles.objetivoFotoCol}>
              <ComparadorImagenes
                imagenAntes={`${BASE}/sucio.webp`}
                imagenDespues={`${BASE}/limpio.webp`}
                altAntes="Tubería con incrustaciones antes del servicio"
                altDespues="Tubería limpia tras el servicio"
                posicionInicial={50}
              />

              {/* Hint de deslizar */}
              <div className={styles.comparadorHint}>
                <span className={styles.comparadorHintIcono}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6"/></svg>
                </span>
                Desliza para ver el resultado del servicio
              </div>

              {/* Pie de foto comparativo */}
              <div className={styles.comparadorCaption}>
                <div className={styles.comparadorCaptionLado}>
                  <span className={styles.comparadorCaptionLabel}>Antes</span>
                  <p className={styles.comparadorCaptionTexto}>Incrustaciones que reducen el flujo y elevan el consumo energético</p>
                </div>
                <div className={styles.comparadorCaptionDivider} />
                <div className={styles.comparadorCaptionLado}>
                  <span className={styles.comparadorCaptionLabel}>Después</span>
                  <p className={styles.comparadorCaptionTexto}>Conducción limpia al 100% del caudal y eficiencia originales</p>
                </div>
              </div>
            </div>
          </div>

          {/* Beneficios en fila debajo del grid */}
          <div className={styles.beneficiosRow}>
            {beneficios.map((b, i) => (
              <div
                key={i}
                className={`${styles.beneficioCard} ${objVis ? styles.cardVis : ''}`}
                style={{ transitionDelay: `${400 + i * 120}ms` }}
              >
                <div className={styles.beneficioIcon}>{b.icon}</div>
                <div>
                  <span className={styles.beneficioValor}>{b.valor}</span>
                  <p className={styles.beneficioDesc}>{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ALCANCE ── */}
      <section ref={alcRef} className={styles.alcanceSection}>
        <div className="container">
          <div className={`${styles.alcanceHeader} ${alcVis ? styles.headerVis : ''}`}>
            <h2 className={styles.secTitleDark}>Equipos que <span className={styles.acento}>intervenimos</span></h2>
            <div className={styles.secLine} />
          </div>
          <div className={styles.equiposGrid}>
            {equipos.map((e, i) => (
              <div
                key={i}
                className={`${styles.equipoCard} ${alcVis ? styles.equipoVis : ''}`}
                style={{ transitionDelay: `${i * 70}ms` }}
              >
                <div className={styles.equipoIcon}>{e.icon}</div>
                <span className={styles.equipoNombre}>{e.nombre}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PROCESO ── */}
      <section ref={procRef} className={styles.procesoSection}>
        <div className="container">
          <div className={`${styles.procesoHeader} ${procVis ? styles.headerVis : ''}`}>
            <h2 className={styles.secTitle}>Cómo <span className={styles.acento}>trabajamos</span></h2>
            <div className={styles.secLine} />
          </div>

          {/* Pasos + foto lateral */}
          <div className={styles.procesoLayout}>
            <div className={styles.pasosGrid}>
              {pasos.map((p, i) => (
                <div
                  key={i}
                  className={`${styles.pasoCard} ${procVis ? styles.pasoVis : ''}`}
                  style={{ transitionDelay: `${i * 100}ms` }}
                >
                  <div className={styles.pasoNum}>{p.num}</div>
                  <div className={styles.pasoContenido}>
                    <span className={styles.pasoSub}>{p.sub}</span>
                    <h3 className={styles.pasoTitulo}>{p.titulo}</h3>
                    <p className={styles.pasoDesc}>{p.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className={`${styles.procesoFotoCol} ${procVis ? styles.procesoFotoVis : ''}`}>
              <div className={styles.procesoFotoWrap}>
                <VideoPlayer 
                  src="/videos/proceso-limpieza.mp4" 
                  wrapperStyle={{ height: '100%' }}
                  videoStyle={{ height: '100%', objectFit: 'cover' }}
                />
              </div>
              <div className={`${styles.tiempoNote} ${procVis ? styles.noteVis : ''}`}>
                <div className={styles.noteIcon}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                    <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
                  </svg>
                </div>
                <p><strong>Duración estimada:</strong> varía según la naturaleza y dureza de las incrustaciones, longitud y diámetro de los tubos, y la cantidad total de tubos a intervenir.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── VIDEO PRINCIPAL ── */}
      <section ref={vidRef} className={styles.videoSection}>
        <div className="container">
          <div className={`${styles.videoHeader} ${vidVis ? styles.headerVis : ''}`}>
            <h2 className={styles.secTitleDark}>Ver el servicio <span className={styles.acento}>en acción</span></h2>
            <div className={styles.secLine} />
          </div>
          <div className={`${styles.videoMainWrap} ${vidVis ? styles.videoGridVis : ''}`}>
            <VideoPlayer src={`${BASE}/vid-01.mp4`} poster={`${BASE}/img-01.jpg`} />
          </div>
          <div className={`${styles.videoDuo} ${vidVis ? styles.videoGridVis : ''}`} style={{ transitionDelay: '250ms' }}>
            <VideoPlayer src={`${BASE}/vid-02.mp4`} poster={`${BASE}/foto-04.jpeg`} />
            <VideoPlayer src={`${BASE}/vid-03.mp4`} poster={`${BASE}/foto-05.jpeg`} />
          </div>
        </div>
      </section>

      {/* ── GALERÍA ── */}
      <section ref={galRef} className={styles.galeriaSection}>
        <div className="container">
          <div className={`${styles.galeriaHeader} ${galVis ? styles.headerVis : ''}`}>
            <h2 className={styles.secTitle}>Registros de <span className={styles.acento}>trabajo real</span></h2>
            <div className={styles.secLine} />
          </div>
          <div className={styles.galeriaGrid}>
            {fotos.map((src, i) => (
              <div
                key={i}
                className={`${styles.fotoWrap} ${galVis ? styles.fotoVis : ''}`}
                style={{ transitionDelay: `${i * 80}ms` }}
                onClick={() => setLightboxSrc(src)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && setLightboxSrc(src)}
                aria-label={`Ver foto ${i + 1} en grande`}
              >
                <img src={src} alt={`Limpieza tubería industrial ${i + 1}`} className={styles.foto} loading="lazy" />
                <div className={styles.fotoOverlayHover}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="28" height="28">
                    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35M11 8v6M8 11h6"/>
                  </svg>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className={styles.ctaSection}>
        <div className="container">
          <div className={styles.ctaCard}>
            <div className={styles.ctaTexto}>
              <h2 className={styles.ctaTitulo}>¿Listo para optimizar sus equipos?</h2>
              <p className={styles.ctaDesc}>Un especialista técnico evaluará su instalación sin costo y le presentará la solución más eficiente para su operación.</p>
            </div>
            <div className={styles.ctaBtns}>
              <a href="#contact" className="btn btn-primary">Solicitar evaluación gratuita</a>
              <Link to="/servicios" className="btn btn-secondary">← Ver todos los servicios</Link>
            </div>
          </div>
        </div>
      </section>

      <ContactSection />

      {/* ── LIGHTBOX ── */}
      {lightboxSrc && (
        <div className={styles.lightboxOverlay} onClick={() => setLightboxSrc(null)}>
          <button
            className={styles.lightboxClose}
            onClick={() => setLightboxSrc(null)}
            aria-label="Cerrar"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="18" height="18">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
          <img
            src={lightboxSrc}
            alt="Vista ampliada"
            className={styles.lightboxImg}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

    </Layout>
  );
};

export default LimpiezaTuberia;
